import { useEffect, useRef, useState } from 'react';
import { signOut } from 'firebase/auth';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { useAuth } from '../../features/onboarding/AuthContext';
import { auth } from '../../lib/firebase';

function initialsFor(displayName: string | null, email: string | null): string {
  const source = displayName || email || '';
  const letters = source
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
  return letters || '?';
}

/**
 * Header avatar chip (TTP-271, redesenhado no piloto de design brutalist) —
 * mostra as iniciais do usuário autenticado num quadrado `bg-plate` (mesmo
 * tratamento do mockup "Triviofy Dashboard"), com um popover ao clicar
 * mostrando e-mail + "Sair". `signOut` flips `useAuth()`'s `user` to `null`;
 * `RequireCompletedOnboarding` (guards.tsx) reage sozinho e redireciona pra
 * `/login` — nenhuma navegação manual aqui.
 */
export function UserMenu() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();

    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  if (!user) {
    return null;
  }

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await signOut(auth);
    } catch {
      setSigningOut(false);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-label="Menu do usuário"
        className="flex h-7 w-7 items-center justify-center bg-plate font-mono text-xs text-plate-foreground"
      >
        {initialsFor(user.displayName, user.email)}
      </button>
      {open ? (
        <div
          ref={panelRef}
          tabIndex={-1}
          className="absolute right-0 top-full z-10 mt-2 flex w-56 flex-col gap-3 border border-border bg-card p-4 shadow-sm outline-none"
        >
          <span title={user.email ?? undefined} className="max-w-full truncate font-mono text-xs text-muted-foreground">
            {user.displayName || user.email}
          </span>
          <Button type="button" variant="outline" size="sm" disabled={signingOut} onClick={handleSignOut}>
            {signingOut ? 'Saindo…' : 'Sair'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
